import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Popover } from './Popover.js'
import { Input } from './Input.js'
import { Text } from './Typography.js'
import { CATEGORY_ICON_KEYS } from '../../lib/icons.js'

export interface IconPickerProps {
  value: string | null
  onChange: (key: string) => void
  invalid?: boolean
  disabled?: boolean
  id?: string
}

/**
 * One of the brand's icons, chosen by looking at it.
 *
 * A category carries an `icon_key` and the API takes any string up to sixty
 * characters, so a text field would satisfy it — and produce a category with a
 * blank square where an icon should be, discovered by a customer rather than by
 * the person who typed it. There are fifty-eight icons and no list of their names
 * anywhere a person can read.
 *
 * The keys come from `lib/icons.ts`, generated from the same folder the icons are
 * copied out of, so a key that cannot be drawn cannot be chosen.
 *
 * **`CATEGORY_ICON_KEYS`, not all of them.** It offered every file in the
 * folder, so a category could be given the High Impact logo, a close cross, a
 * chevron, one of the phone's bottom-tab icons, or a microphone with a line
 * through it. That is a list of what exists rather than a list of what to
 * choose from. The generator says which kinds come out and why.
 */
/** `wellness-food` is not what the icon is called out loud. */
const readable = (key: string) => {
  const words = key.replace(/-/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function IconPicker({ value, onChange, invalid, disabled, id }: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const shown = CATEGORY_ICON_KEYS.filter((k) => k.includes(query.trim().toLowerCase()))

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setQuery('')
      }}
      trigger={
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex h-10 w-full items-center gap-3 rounded-md border bg-surface px-4 text-left text-body',
            'outline-none transition-colors focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
            'disabled:bg-sunk disabled:cursor-not-allowed',
            invalid ? 'border-error' : 'border-hairline-strong',
            !value && 'text-ink-muted',
          )}
        >
          {value ? (
            <>
              <img src={`/icons/${value}.svg`} width={18} height={18} alt="" />
              <span className="flex-1 truncate">{readable(value)}</span>
            </>
          ) : (
            <span className="flex-1">Choose an icon</span>
          )}
        </button>
      }
    >
      <div className="w-72">
        <Input
          autoFocus
          size="sm"
          value={query}
          placeholder="Type to narrow it"
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="mt-2 grid max-h-64 grid-cols-6 gap-1 overflow-y-auto">
          {shown.map((k) => (
            <button
              key={k}
              type="button"
              title={readable(k)}
              aria-label={readable(k)}
              aria-pressed={k === value}
              onClick={() => {
                onChange(k)
                setOpen(false)
              }}
              className={cn(
                'flex aspect-square items-center justify-center rounded-sm outline-none',
                'hover:bg-sunk focus-visible:ring-2 focus-visible:ring-coral-fill/30',
                k === value && 'bg-coral-soft ring-1 ring-coral-fill',
              )}
            >
              <img src={`/icons/${k}.svg`} width={18} height={18} alt="" />
            </button>
          ))}
          {shown.length === 0 && (
            <Text variant="secondary" className="col-span-6 p-3">
              No icon is called that.
            </Text>
          )}
        </div>
      </div>
    </Popover>
  )
}
